import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box, Typography, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AlertSubscriptionList from '../components/AlertCenter/AlertSubscriptionList/AlertSubscriptionList';
import AlertSubscriptionKPIs from '../components/AlertCenter/AlertSubscriptionKPIs';
import AlertSubscriptionInfoModal from '../components/AlertCenter/AlertSubscriptionInfoModal';
import inputSignalsService from 'services/inputSignalsService';
import useInputSignalRules from 'hooks/useInputSignalRules';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';

const AlertCenter = () => {
	const { tenant } = useIRWorkstation();
	const { tenantId } = useParams();
	const navigate = useNavigate();
	const { trackButtonClick, trackPageLoad } = useTrackEvent({ tenant, product: 'IEM' });

	const [showExplanationModal, setShowExplanationModal] = useState(false);
	const [inputSignalStatsLoading, setInputSignalStatsLoading] = useState(true);
	const [alertsReceivedCount, setAlertsReceivedCount] = useState(null);

	const {
		subscribedAlerts,
		allAlerts,
		loading,
		filter,
		setFilter,
		error,
		subscribe,
		unsubscribe,
		submitDelete
	} = useInputSignalRules();

	// Fetch input signal stats for KPI card
	useEffect(() => {
		const fetchInputSignalStats = async () => {
			setInputSignalStatsLoading(true);
			try {
				const response = await inputSignalsService.getInputSignalsStats();
				setAlertsReceivedCount(
					response?.alerts_received_last_30_days_count || 0
				);
			} catch (error) {
				console.error('Failed to fetch input signal stats:', error);
			} finally {
				setInputSignalStatsLoading(false);
			}
		};

		fetchInputSignalStats();
	}, [tenantId, alertsReceivedCount]);

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.AlertCenter);
	}, []);

	const handleButtonClickTrackEvent = (targetKey) => {
		const targetMap = {
			explainSubscriptions: ButtonClickTargets.AlertCenterExplainSubscriptions,
			newAlert: ButtonClickTargets.AlertCenterNewAlert,
			subscribed: ButtonClickTargets.AlertCenterSubscribedFilter,
			all: ButtonClickTargets.AlertCenterAllFilter,
			subscribeToggle: ButtonClickTargets.AlertCenterSubscribeToggle,
			unsubscribeToggle: ButtonClickTargets.AlertCenterUnsubscribeToggle,
			delete: ButtonClickTargets.AlertCenterDelete
		};

		const target = targetMap[targetKey];
		if (target) {
			trackButtonClick(target);
		}
	};

	const handleSubscribeToggle = (alert) => {
		handleButtonClickTrackEvent('subscribeToggle');
		subscribe(alert);
	};

	const handleUnsubscribeToggle = (alert) => {
		handleButtonClickTrackEvent('unsubscribeToggle');
		unsubscribe(alert);
	};

	const handleDelete = (alert) => {
		handleButtonClickTrackEvent('delete');
		submitDelete(alert);
	};
	

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				overflowY: 'auto',
				backgroundColor: 'background.default',
				padding: '12px 20px',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			{/* page header */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'baseline',
					justifyContent: 'space-between',
				}}
			>
				<Box>
					<Typography sx={{ fontSize: '17px', fontWeight: 700 }}>
						Alert Center
					</Typography>
					<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
						Your Alert Subscriptions within the Investor Engagement Manager
					</Typography>
				</Box>

				<Box
					sx={{
						display: 'flex',
						gap: 2,
					}}
				>
					<Button
						onClick={() => {
							handleButtonClickTrackEvent('explainSubscriptions');
							setShowExplanationModal(true);
						}}
						variant='contained'
						sx={{
							backgroundColor: 'status.blue.bg',
							color: 'status.blue.text',
							border: '0.5px solid',
							borderColor: 'primary.main',
							boxShadow: 'none',
							'&:hover': {
								backgroundColor: 'status.blue.hover',
								boxShadow: 'none',
							},
						}}
					>
						Explain alert subscriptions
					</Button>
					<Button
						variant='contained'
						startIcon={<AddIcon />}
						onClick={() => {
							handleButtonClickTrackEvent('newAlert');
							navigate(`/tenants/${tenantId}/workstation/alert_center/new`);
						}}
					>
						New Alert
					</Button>
				</Box>
			</Box>

			{/* activity summary */}
			<Box>
				<AlertSubscriptionKPIs
					subscribedAlerts={subscribedAlerts}
					loading={loading || inputSignalStatsLoading}
					alertsReceivedCount={alertsReceivedCount}
				/>
			</Box>

			{/* alert list container  */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
				}}
			>
				<AlertSubscriptionList
					subscribedAlerts={subscribedAlerts}
					allAlerts={allAlerts}
					loading={loading || inputSignalStatsLoading}
					subscribe={(alert) => handleSubscribeToggle(alert)}
					unsubscribe={(alert) => handleUnsubscribeToggle(alert)}
					handleDelete={(alert) => handleDelete(alert)}
					filter={filter}
					setFilter={setFilter}
					error={error}
					onFilterClick={(filterValue) => {
						handleButtonClickTrackEvent(filterValue);
					}}
				/>
			</Box>

			<AlertSubscriptionInfoModal
				open={showExplanationModal}
				onClose={() => setShowExplanationModal(false)}
			/>
		</Box>
	);
};

export default AlertCenter;
